import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../auth/AuthProvider";
import {
  getSystemHealth,
  getGridSyncStatus,
  getGridImpactCoverage,
  getTaskMonitoring,
  getSyncHeatmap,
  getHeatmapHistory,
  getSyncSources,
  type HealthResponse,
  type GridSyncResponse,
  type GridImpactResponse,
  type TaskMonitoringResponse,
  type SyncHeatmapResponse,
  type HeatmapHistoryResponse,
  type SyncSourcesResponse,
  type GridSource,
} from "../../../api/monitoring";

export function useSystemHealth() {
  const { token } = useAuth();
  return useQuery<HealthResponse>({
    queryKey: ["admin", "monitoring", "health"],
    queryFn: () => getSystemHealth(token!),
    enabled: !!token,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

export function useGridSyncStatus() {
  const { token } = useAuth();
  return useQuery<GridSyncResponse>({
    queryKey: ["admin", "monitoring", "grid-sync"],
    queryFn: () => getGridSyncStatus(token!),
    enabled: !!token,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useGridImpactCoverage() {
  const { token } = useAuth();
  return useQuery<GridImpactResponse>({
    queryKey: ["admin", "monitoring", "grid-impacts"],
    queryFn: () => getGridImpactCoverage(token!),
    enabled: !!token,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useTaskMonitoring() {
  const { token } = useAuth();
  return useQuery<TaskMonitoringResponse>({
    queryKey: ["admin", "monitoring", "tasks"],
    queryFn: () => getTaskMonitoring(token!),
    enabled: !!token,
    refetchInterval: 15_000,
    staleTime: 10_000,
  });
}

export function useSyncHeatmap(days: number = 30, source: GridSource = "eia") {
  const { token } = useAuth();
  return useQuery<SyncHeatmapResponse>({
    queryKey: ["admin", "monitoring", "grid-sync-heatmap", days, source],
    queryFn: () => getSyncHeatmap(token!, days, source),
    enabled: !!token,
    refetchInterval: 300_000,
    staleTime: 120_000,
  });
}

export function useHeatmapHistory(source: GridSource = "all") {
  const { token } = useAuth();
  return useQuery<HeatmapHistoryResponse>({
    queryKey: ["admin", "monitoring", "heatmap-history", source],
    queryFn: async () => {
      if (source !== "all") {
        return getHeatmapHistory(token!, source);
      }

      // Unified: fetch all sources in parallel, merge with region prefixes
      const sources: Array<{ key: "eia" | "entsoe" | "bmrs" | "lmp"; prefix: string }> = [
        { key: "eia", prefix: "US" },
        { key: "entsoe", prefix: "EU" },
        { key: "bmrs", prefix: "UK" },
        { key: "lmp", prefix: "LMP" },
      ];
      const results = await Promise.allSettled(
        sources.map((s) => getHeatmapHistory(token!, s.key))
      );

      const mergedBands: Record<string, HeatmapHistoryResponse["bands"][0]> = {};
      const allBaCodes = new Set<string>();
      const mergedErrors: string[] = [];

      results.forEach((result, i) => {
        const sourceKey = sources[i].key;

        if (result.status !== "fulfilled") {
          // A rejected request (network error, non-2xx) is a real failure,
          // not "no data collected yet" — surface it, don't drop it.
          mergedErrors.push(`${sourceKey}: request failed`);
          return;
        }
        if (result.value?.errors?.length) {
          // Backend reported HTTP 200 with a degraded/partial payload.
          mergedErrors.push(...result.value.errors.map((e) => `${sourceKey}: ${e}`));
        }
        if (!result.value?.bands) return;
        const prefix = sources[i].prefix;

        for (const band of result.value.bands) {
          if (!mergedBands[band.resolution]) {
            mergedBands[band.resolution] = {
              resolution: band.resolution,
              data: [],
              periods: [],
            };
          }
          const merged = mergedBands[band.resolution];
          const periodSet = new Set(merged.periods);

          for (const d of band.data) {
            const prefixedBa = `${prefix}:${d.ba_code}`;
            merged.data.push({ ...d, ba_code: prefixedBa });
            allBaCodes.add(prefixedBa);
            periodSet.add(d.period);
          }
          merged.periods = Array.from(periodSet).sort();
        }
      });

      const bands = Object.values(mergedBands);
      const resOrder: Record<string, number> = { yearly: 0, monthly: 1, daily: 2 };
      bands.sort((a, b) => (resOrder[a.resolution] ?? 99) - (resOrder[b.resolution] ?? 99));

      return {
        source: "all",
        bands,
        ba_codes: Array.from(allBaCodes).sort(),
        errors: mergedErrors,
        checked_at: new Date().toISOString(),
      };
    },
    enabled: !!token,
    refetchInterval: 300_000,
    staleTime: 120_000,
  });
}

export function useSyncSources() {
  const { token } = useAuth();
  return useQuery<SyncSourcesResponse>({
    queryKey: ["admin", "monitoring", "grid-sync-sources"],
    queryFn: () => getSyncSources(token!),
    enabled: !!token,
    refetchInterval: 300_000,
    staleTime: 120_000,
  });
}
